import React from "react";
import Container from "components/Docs/Container";
import { NAV_CONFIG_PATH, getIndexStaticProps } from "lib/mdx";
import classnames from "classnames";
import Link from "next/link";
import {
  RiDoorOpenLine,
  RiShieldKeyholeLine,
  RiErrorWarningLine,
  RiLinksLine,
  RiFileList3Line,
  RiDiscordLine,
  RiApps2Line
} from "react-icons/ri";
import Padder from "components/Padder";
import { cellGrow } from "constants/styles";
import { styles } from "components/Landing/HeroSection/styles";
import { getJWT } from "utils/auth";
import APIKeysAccessPrompt from "components/APIKeysAccessPrompt";
import { useAuthedDocsPreview } from "utils/hooks/docs";
import isEmpty from "lodash/isEmpty";
import Pill from "components/Pill";
import { STATUS, UI_THEME } from "utils/constants/ui";
import PATHS from "utils/paths";
import ZoneIconLink from "components/ZoneLinkNode";
import { DISCORD_SERVER_INVITE_URL, HUB_DEV_SETTINGS } from "constants/docs";
import { CURRENT_VERSION_LABEL } from "utils/constants/version";

const FILES_PATH = "docs";

const DOCS_GRID_CONFIG = [
  {
    id: "intro",
    Icon: RiDoorOpenLine,
    label: "Intro",
    description: "An overview of the API and what you can do with it.",
    link: {
      as: "/introduction/overview",
      href: "/introduction/overview"
    }
  },
  {
    id: "authentication",
    Icon: RiShieldKeyholeLine,
    label: "Authentication",
    description:
      "How to access and use your API keys to authenticate with PriceBlocs.",
    link: {
      as: "/authentication/overview",
      href: "/authentication/[slug]"
    }
  },
  {
    id: "errors",
    Icon: RiErrorWarningLine,
    label: "Errors",
    description: "Standard errors to be aware of when working with the API.",
    link: {
      as: "/errors/overview",
      href: "/errors/[slug]"
    }
  },
  {
    id: "paymentLinks",
    Icon: RiLinksLine,
    label: "Payment links",
    description: "How to create and manage payment links via our REST API.",
    link: {
      as: "/payments/payment-links/overview",
      href: "/payments/payment-links/[slug]"
    }
  },
  {
    id: "checkouts",
    Icon: RiFileList3Line,
    label: "Checkouts",
    description: "Fetch Checkout records related to your Payment Links.",
    link: {
      as: "/payments/checkouts/overview",
      href: "/payments/checkouts/[slug]"
    }
  },
  {
    id: "community",
    Icon: RiDiscordLine,
    label: "Community",
    description: "Have questions or feedback? Join the community on Discord.",
    link: {
      as: DISCORD_SERVER_INVITE_URL,
      href: DISCORD_SERVER_INVITE_URL
    }
  },
  {
    id: "usage",
    Icon: RiApps2Line,
    label: "Usage",
    description: "Learn about usage restrictions and fees for the API.",
    link: {
      as: "/usage/overview",
      href: "/usage/[slug]"
    }
  }
];

const Docs = ({ navConfig }) => {
  const { preview: authedPreview } = useAuthedDocsPreview({
    pathname: "introduction/overview"
  });
  const needsTestKeys = isEmpty(authedPreview);
  const isUser = Boolean(getJWT());

  const zoneLinkProps = isUser
    ? {
        href: HUB_DEV_SETTINGS,
        copy: "Dashboard"
      }
    : {
        href: PATHS.SIGNUP,
        copy: "Signup"
      };

  return (
    <Container navConfig={navConfig}>
      <div className="w-100 overflow-y-scroll-l ph3-ns">
        <div className="w-100 pv4 pv5-l mw8 w-100 center">
          <div className="flex flex-row items-center">
            <div className="dib hero__brand--priceblocs w-100 z-1">
              <div className="hero__logo contain bg-left h-100 w-100" />
            </div>
            <ZoneIconLink {...zoneLinkProps} />
          </div>
          <div className="pt3 pt4-l pb4">
            <div className="flex flex-row items-center pb3 pt2">
              <h1 className="fw6 f3 f2-l pa0 ma0 ">Documentation</h1>
              <Pill theme={UI_THEME.SLIM} copy={CURRENT_VERSION_LABEL} />
            </div>
            <h3 className="f5 f4-l pt0 fw5 ma0 pa0 lh-copy">
              Powerful payment pages and links, backed by Stripe Checkout, just
              an API call away. Get up and running with our snippets and
              reference documentation.
            </h3>
          </div>
          <div className="grid-container">
            {DOCS_GRID_CONFIG.map(
              (
                { id, label, description, Icon, link, callout, disabled },
                gridIx
              ) => {
                return (
                  <Link {...link} key={gridIx}>
                    <div
                      className={classnames(
                        `${id} flex flex-column w-100 ba br2 hairline-1 pa3 mb3 mb-ns relative`,
                        {
                          "pointer cell-grow bg-white--hover ba--primary--hover": !disabled,
                          "o-90 cursor-not-allowed": disabled
                        }
                      )}
                    >
                      <div className="flex flex-row items-center">
                        <Icon size={20} />
                        <div
                          className="f4 fw6 pl2 color-primary lh-title"
                          style={{ marginTop: 2 }}
                        >
                          {label}
                        </div>
                      </div>
                      <div className="f6 lh-copy pt2 mt1">{description}</div>
                    </div>
                  </Link>
                );
              }
            )}
          </div>
          <div className="bt hairline-1 w-100 pt4">
            <APIKeysAccessPrompt
              customClasses={{
                container:
                  "roll-ci pa2 hairline-1 ba br2 flex flex-column justify-between color-text--primary bg-white--hover"
              }}
              needsTestKeys={needsTestKeys}
              isUser={isUser}
            />
          </div>
        </div>
        <Padder theme="visible" />
        <style jsx>{`
          @media (max-width: 30em) {
            .grid-container {
              display: flex;
              flex-direction: column;
            }
          }
          @media (min-width: 30em) {
            .grid-container {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              grid-template-rows: repeat(2, 1fr);
              gap: 8px 8px;
              grid-template-areas:
                "intro account merchant-account"
                "resources links checkout"
                "content sales customers"
                "billing . .";
            }
          }

          // Row 1
          .intro {
            grid-area: intro;
          }

          .account {
            grid-area: account;
          }

          .merchant-account {
            grid-area: merchant-account;
          }

          // Row 2
          .resources {
            grid-area: resources;
          }

          .checkout {
            grid-area: checkout;
          }

          .links {
            grid-area: links;
          }

          // Row 3
          .content {
            grid-area: content;
          }

          .sales {
            grid-area: sales;
          }

          .customers {
            grid-area: customers;
          }

          // Row 4
          .billing {
            grid-area: billing;
          }
        `}</style>
        <style jsx>{cellGrow}</style>
        <style jsx>{styles}</style>
      </div>
    </Container>
  );
};

export const getStaticProps = getIndexStaticProps(FILES_PATH, NAV_CONFIG_PATH);

export default Docs;
